/* eslint-disable linebreak-style, camelcase */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useAmp } from 'next/amp';
import getSiteDomain from 'utils/getSiteDomain';
import uuid from 'uuid/v4';
import useDeviceScreen from 'root/hooks/useDeviceScreen';
import AmpAdSlot from 'components/AdManager/AmpAdSlot';
import { useAds } from 'components/AdManager/AdsContext';
import { intl } from 'components/LocaleProvider';
import isBrowser from 'utils/isBrowser';

import messages from './messages';

const getConfig = require('next/config').default;

const { publicRuntimeConfig = {} } = getConfig ? getConfig() : {};

const AdSlot = dynamic(() => import('./AdSlot'), { ssr: false });

// Remove querystring, hash and /amp from URL path
const getAmpUrl = (path) => path?.split(/[?#]/)[0].replace('/amp', '');

const AdManager = ({ containerId, type, className, display, targets, keepSizeOnRefresh }) => {
  const router = useRouter();
  const { isPhone, isTablet, isDesktop } = useDeviceScreen();

  const adSlotRef = useRef(containerId || `ad-slot-${uuid()}`);
  const isAmp = useAmp();
  const { getPosition } = useAds();
  let adType = type;

  const getEnvTarget = () => {
    if (isAmp) return 'amp';
    if (isDesktop) return 'desktop';
    return 'mobile';
  };

  const sizes = {
    desktop: null,
    mobile: '[[300, 100], [300, 250], [300, 300], [300, 600], [320, 50], [320, 100]]',
  };

  const ampSizes = {
    width: isDesktop ? 970 : 336,
    height: isDesktop ? 90 : 600,
    desktop: '970x250,970x180,970x90,728x90',
    mobile: '300x250,300x100,320x50,300x300,200x200,320x100,320x320,240x400,300x600,320x480,336x280,270x480,300x200,320x240',
  };

  switch (adType) {
    case 'billboard':
    case 'grid':
    case 'large':
      sizes.desktop = '[[970, 90], [980, 250], [970, 250],[970, 180], [728, 90]]';
      ampSizes.desktop = '980x250,970x250,980x90,970x90,970x180,728x90';
      break;

    case 'box':
      sizes.desktop = '[[300, 250], [336, 280], [600, 180], [600, 90]]';
      ampSizes.desktop = '600x90,336x280,300x250';
      ampSizes.width = isDesktop ? 300 : ampSizes.width;
      ampSizes.height = isDesktop ? 180 : ampSizes.height;
      break;

    case 'sky':
      sizes.desktop = '[[300, 250], [300, 300], [300, 600]]';
      ampSizes.desktop = '300x600,300x250';
      ampSizes.width = isDesktop ? 300 : ampSizes.width;
      ampSizes.height = isDesktop ? 250 : ampSizes.height;

      // Change type to box on mobile
      if (!isDesktop) {
        adType = 'box';
      }
      break;

    case 'medium':
      sizes.desktop = '[[600, 90], [600, 300], [600, 338], [600, 180]]';
      sizes.mobile = '[[300, 100], [300, 250], [320, 50], [320, 100]]';
      ampSizes.desktop = '600x338,600x180,600x90';
      ampSizes.mobile = '320x100,320x50,300x250';
      ampSizes.width = isDesktop ? 600 : ampSizes.width;
      ampSizes.height = isDesktop ? 300 : ampSizes.height;
      break;

    case 'side':
      sizes.desktop = '[[160, 600], [140, 800], [240, 900], [310, 1000]]';
      sizes.mobile = null;
      ampSizes.desktop = '310x1000,240x900,140x800';
      ampSizes.mobile = null;
      ampSizes.width = isDesktop ? 160 : ampSizes.width;
      ampSizes.height = isDesktop ? 600 : ampSizes.height;
      break;

    default:
      break;
  }

  if (useAmp()) {
    return (
      <React.Fragment>
        {((isPhone && display.mobile) || (isTablet && display.tablet)
          || (isDesktop && display.desktop)) && (
          <AmpAdSlot
            unit={publicRuntimeConfig.AD_UNIT_ID}
            containerId={adSlotRef.current}
            ampSizes={ampSizes}
            targets={{
              ...targets,
              type: adType,
              position: getPosition({
                containerId: adSlotRef.current,
                type: adType,
              }),
              adOrder: `amp_${getPosition({
                containerId: adSlotRef.current,
                type: adType,
              })}`,
              source: 'google',
              medium: 'organic',
              locale: publicRuntimeConfig.APP_LOCALE,
              env: 'amp',
              userChannel: 'amp',
              ms: ampSizes.mobile,
              vertical: publicRuntimeConfig.analytics_datasource,
              domain: getSiteDomain(publicRuntimeConfig.wp_site_url),
              tenant: getSiteDomain(publicRuntimeConfig.wp_site_url),
              url: getAmpUrl(router?.asPath)
            }}
            className={`${className} ad-space`}
          />
        )}
      </React.Fragment>
    );
  }

  if (isBrowser()) {
    return (
      <React.Fragment>
        {((isPhone && display.mobile) || (isTablet && display.tablet)
          || (isDesktop && display.desktop)) && (
          <React.Fragment>
            <AdSlot
              type={adType}
              containerId={adSlotRef.current}
              className={className}
              unit={`${publicRuntimeConfig.AD_UNIT_ID}`}
              desktopSizes={sizes.desktop}
              mobileSizes={sizes.mobile}
              targets={{
                ...targets,
                env: getEnvTarget(),
                ms: isDesktop ? ampSizes.desktop : ampSizes.mobile,
                domain: getSiteDomain(publicRuntimeConfig.wp_site_url),
                tenant: getSiteDomain(publicRuntimeConfig.wp_site_url),
                locale: publicRuntimeConfig.APP_LOCALE,
                vertical: publicRuntimeConfig.analytics_datasource,
              }}
              keepSizeOnRefresh={keepSizeOnRefresh}
            />

            { /* language=CSS */}
            <style jsx global>
              {`
                #single .ad-space:empty {
                    display: none;
                }
                .ad-space::before {
                  content: '${intl.formatMessage(messages.advertising)}';
                  text-align: center;
                  display: block;
                  white-space: nowrap;
                  font-size: 10px;
                  line-height: 15px;
                  letter-spacing: 1px;
                  color: #6d6d6e;
                  text-transform: uppercase;
                  margin: 0 auto;
                }
              `}
            </style>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  return null;
};

AdManager.propTypes = {
  containerId: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  display: PropTypes.object,
  targets: PropTypes.object,
  keepSizeOnRefresh: PropTypes.bool,
};

AdManager.defaultProps = {
  className: 'ad-space',
  display: {
    mobile: true,
    tablet: true,
    desktop: true,
  },
  keepSizeOnRefresh: false,
};

export default AdManager;
